/* eslint-disable complexity */
import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Pill from 'carbon-react/lib/components/pill';

import { ExtendedReportList } from '@futrli/views/processors/reports/reportsList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getReportingEditReportUrl, matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import { ResourceAccessLabel } from '@futrli/predict/sharing/components/resource-access-label/resource-access-label';

import { ReportActionsPopover } from './report-actions-popover';
import { ReportDialogType } from './my-reports-tab';

export const ROW_HEIGHT_DEFAULT = 86;
export const ROW_HEIGHT_WITH_LOCKED_PILL = 104;

const getStatusBadge = (
  report: ExtendedReportList,
  intl: ReturnType<typeof useIntl>,
  isResourceSharingEnabled: boolean
) => {
  if (isResourceSharingEnabled) {
    if (report.permission_info?.sharing_status === 'shared') {
      return (
        <Pill colorVariant="positive" size="S" fill>
          {intl.formatMessage({ id: 'common.shared', defaultMessage: 'Shared' })}
        </Pill>
      );
    }

    return (
      <Pill size="S" pillRole="status">
        {intl.formatMessage({ id: 'common.private', defaultMessage: 'Private' })}
      </Pill>
    );
  }

  return report.status === 'live' ? (
    <Pill colorVariant="positive" size="S" fill>
      {intl.formatMessage({ id: 'common.published', defaultMessage: 'Published' })}
    </Pill>
  ) : (
    <Pill size="S" pillRole="status">
      {intl.formatMessage({ id: 'common.draft', defaultMessage: 'Draft' })}
    </Pill>
  );
};

interface MyReportRowProps {
  report: ExtendedReportList;
  onDialogAction: (type: ReportDialogType, report: ExtendedReportList) => void;
  isResourceSharingEnabled?: boolean;
}

export const MyReportRow = ({ report, onDialogAction, isResourceSharingEnabled = false }: MyReportRowProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const isScheduled = !!report.next_scheduled_at;
  const formattedDate = intl.formatDate(report.created, { day: '2-digit', month: 'short', year: 'numeric' });

  const handleNameClick = useCallback(() => {
    navigate(getReportingEditReportUrl(workspaceId, organisationId, report.id, isConsolidation));
  }, [navigate, workspaceId, organisationId, report.id, isConsolidation]);

  const orientationLabel =
    report.orientation === 'landscape' ? (
      <FormattedMessage id="reporting.landscape" defaultMessage="Landscape" />
    ) : report.orientation === 'portrait' ? (
      <FormattedMessage id="reporting.portrait" defaultMessage="Portrait" />
    ) : (
      <FormattedMessage id="reporting.dashboard" defaultMessage="Dashboard" />
    );

  return (
    <ListingPageRowLayout
      nameColumn={
        <ListingPageRowNameCell
          name={report.name}
          onNameClick={handleNameClick}
          subtext={orientationLabel}
          pill={
            report.is_locked && !isResourceSharingEnabled ? (
              <Pill pillRole="status" size="S">
                {intl.formatMessage({ id: 'reporting.locked', defaultMessage: 'Locked' })}
              </Pill>
            ) : undefined
          }
        />
      }
      metrics={
        <>
          <ListingPageRowMetricCell
            size="default"
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={getStatusBadge(report, intl, isResourceSharingEnabled)}
          />
          {isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              size="default"
              label={<FormattedMessage id="predict.access" defaultMessage="Access" />}
              value={<ResourceAccessLabel permission={report.permission_info?.effective_permission} />}
            />
          )}
          <ListingPageRowMetricCell
            size="pages"
            label={<FormattedMessage id="predict.pages" defaultMessage="Pages" />}
            value={report.page_count}
          />
          {!isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              size="default"
              label={<FormattedMessage id="predict.access" defaultMessage="Access" />}
              value={
                report.is_locked ? (
                  <FormattedMessage id="predict.read_only" defaultMessage="Read only" />
                ) : (
                  <FormattedMessage id="common.edit" defaultMessage="Edit" />
                )
              }
            />
          )}
          <ListingPageRowMetricCell
            size="default"
            label={<FormattedMessage id="predict.date_created" defaultMessage="Date created" />}
            value={formattedDate}
          />
          <ListingPageRowMetricCell
            size="default"
            label={<FormattedMessage id="predict.scheduled" defaultMessage="Scheduled" />}
            value={
              isScheduled ? (
                <FormattedMessage id="predict.yes" defaultMessage="Yes" />
              ) : (
                <FormattedMessage id="predict.no" defaultMessage="No" />
              )
            }
          />
          <ListingPageRowMetricCell
            size="wide"
            label={<FormattedMessage id="common.owner" defaultMessage="Owner" />}
            value={
              report.isOwner ? intl.formatMessage({ id: 'common.you', defaultMessage: 'You' }) : report.ownerName || '—'
            }
          />
        </>
      }
      actions={<ReportActionsPopover report={report} onDialogAction={onDialogAction} />}
    />
  );
};
